import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { MapPin, Heart, Users, MessageCircle, Sparkles, Navigation, AlertTriangle } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
}

const Landing = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = React.useContext(AuthContext);

  const fetchAddress = useCallback(async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      setAddress(response.data.display_name);
    } catch (error) {
      setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  }, []);

  const handleMapClick = useCallback((pos) => {
    setPosition(pos);
    setLocationError('');
    fetchAddress(pos[0], pos[1]);
  }, [fetchAddress]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition([lat, lng]);
        setLocationError('');
        fetchAddress(lat, lng);
        setLocationLoading(false);
        toast.success('Location detected!');
      },
      () => {
        toast.error('Could not get your location. Please click on the map instead.');
        setLocationLoading(false);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // For registration, enforce location
    if (!isLogin && !position) {
      setLocationError('Location is required to create your profile. Please use "Detect My Location" or click on the map to set your position.');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            location: {
              lat: position[0],
              lng: position[1],
              address: address || `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`
            }
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);

      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If this email is registered, you will receive a reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background with Hero Image */}
      <div
        className="absolute inset-0 bg-cover bg-no-repeat"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.25)), url(https://images.unsplash.com/photo-1633442336558-a447c764911a?w=1920&q=80)',
          backgroundPosition: 'center 60%',
          animation: 'fadeIn 1.5s ease-in'
        }}
      />

      {/* Floating Hearts Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Heart className="absolute text-red-300/20 animate-float" style={{ top: '10%', left: '10%', width: '40px', height: '40px', animationDelay: '0s' }} />
        <Heart className="absolute text-pink-300/20 animate-float" style={{ top: '60%', left: '80%', width: '30px', height: '30px', animationDelay: '2s' }} />
        <Heart className="absolute text-red-200/20 animate-float" style={{ top: '40%', left: '15%', width: '35px', height: '35px', animationDelay: '4s' }} />
        <Heart className="absolute text-pink-200/20 animate-float" style={{ top: '80%', left: '70%', width: '25px', height: '25px', animationDelay: '1s' }} />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center mb-16 animate-slideDown">
            {/* Logo with glow effect */}
            <div className="inline-block mb-6 relative group">
              <div className="absolute inset-0 bg-teal-400 blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
              <div
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="relative px-8 py-4 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white logo-text flex items-center gap-3">
                  <Sparkles className="w-10 h-10 animate-pulse" />
                  STRAY SPOTTER
                  <Sparkles className="w-10 h-10 animate-pulse" />
                </h1>
              </div>
            </div>

            <p className="text-xl md:text-2xl text-white font-semibold mb-4 drop-shadow-lg animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              Saving Lives Together
            </p>

            <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fadeIn text-white drop-shadow-2xl" style={{ animationDelay: '0.5s' }}>
              SPOT, REPORT, RESCUE
              <br />
              <span style={{ color: 'rgb(26, 160, 157)' }} className="drop-shadow-xl">SAVE STRAY ANIMALS</span>
            </h2>

            <p className="text-lg md:text-xl text-gray-100 max-w-3xl mx-auto mb-8 leading-relaxed drop-shadow-md animate-fadeIn" style={{ animationDelay: '0.7s' }}>
              Connect rescuers and animal lovers in your community. Spot an animal in
              need? Share its location and let nearby helpers know. Every life matters.
            </p>

            {!showAuth && (
              <Button
                onClick={() => setShowAuth(true)}
                data-testid="get-started-btn"
                size="lg"
                className="text-lg px-8 py-6 shadow-2xl transform hover:scale-110 transition-all duration-300 animate-bounce"
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
              >
                <Heart className="w-5 h-5 mr-2 animate-pulse" />
                Get Started - Save A Life Today
              </Button>
            )}
          </div>

          {/* Auth Card */}
          {showAuth && (
            <div className={`mx-auto animate-scaleIn ${isLogin ? 'max-w-md' : 'max-w-2xl'}`}>
              <Card className="shadow-2xl border-0 backdrop-blur-sm bg-white/95">
                <CardHeader className="text-center pb-4">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center animate-pulse">
                    <Heart className="w-10 h-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold" data-testid="auth-title">
                    {isLogin ? 'Welcome Back, Hero!' : 'Join Our Rescue Mission'}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {isLogin
                      ? 'Continue helping animals in need'
                      : 'Every hero starts with a single step'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                      <div className="space-y-2 animate-slideRight">
                        <Label htmlFor="name" className="text-base font-medium">Your Name</Label>
                        <Input
                          id="name"
                          data-testid="register-name-input"
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required={!isLogin}
                          className="border-2 focus:border-teal-400 transition-colors"
                        />
                      </div>
                    )}

                    <div className="space-y-2 animate-slideRight" style={{ animationDelay: '0.1s' }}>
                      <Label htmlFor="email" className="text-base font-medium">Email</Label>
                      <Input
                        id="email"
                        data-testid="auth-email-input"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="border-2 focus:border-teal-400 transition-colors"
                      />
                    </div>

                    <div className="space-y-2 animate-slideRight" style={{ animationDelay: '0.2s' }}>
                      <Label htmlFor="password" className="text-base font-medium">Password</Label>
                      <Input
                        id="password"
                        data-testid="auth-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        className="border-2 focus:border-teal-400 transition-colors"
                      />
                      {isLogin && (
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          data-testid="forgot-password-link"
                          className="text-sm text-teal-600 hover:text-teal-700 hover:underline float-right mt-1"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>

                    {/* LOCATION PICKER — Registration only */}
                    {!isLogin && (
                      <div className="space-y-3 animate-slideRight" style={{ animationDelay: '0.3s' }}>
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-medium flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                            Your Location <span className="text-red-500">*</span>
                          </Label>
                          <Button
                            type="button"
                            onClick={handleUseCurrentLocation}
                            data-testid="detect-location-btn"
                            variant="outline"
                            size="sm"
                            disabled={locationLoading}
                            style={{ color: 'rgb(26, 160, 157)', borderColor: 'rgb(26, 160, 157)' }}
                            className="hover:bg-teal-50 text-xs"
                          >
                            {locationLoading ? (
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                                Detecting...
                              </div>
                            ) : (
                              <>
                                <Navigation className="w-3 h-3 mr-1" />
                                Detect My Location
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="h-48 rounded-lg overflow-hidden border-2 border-gray-200" data-testid="register-location-map">
                          <MapContainer
                            center={position || [25.2048, 55.2708]}
                            zoom={position ? 13 : 2}
                            className="h-full w-full"
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <LocationMarker position={position} setPosition={handleMapClick} />
                          </MapContainer>
                        </div>

                        {position && (
                          <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(26, 160, 157, 0.1)' }}>
                            <p className="text-gray-700 font-medium flex items-center gap-1">
                              <MapPin className="w-3 h-3" style={{ color: 'rgb(26, 160, 157)' }} />
                              {address || 'Fetching address...'}
                            </p>
                          </div>
                        )}

                        {!position && (
                          <p className="text-xs text-gray-500">
                            Click "Detect My Location" or tap on the map to set your position.
                          </p>
                        )}

                        {/* Location error message */}
                        {locationError && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="location-error-msg">
                            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700 font-medium">{locationError}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      type="submit"
                      data-testid="auth-submit-btn"
                      className="w-full text-base py-6 shadow-lg transform hover:scale-105 transition-all duration-300"
                      style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </div>
                      ) : (
                        <>
                          <Heart className="w-5 h-5 mr-2" />
                          {isLogin ? 'Sign In & Help' : 'Join The Mission'}
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setFormData({ name: '', email: '', password: '' });
                        setPosition(null);
                        setAddress('');
                        setLocationError('');
                      }}
                      data-testid="toggle-auth-mode-btn"
                      className="text-teal-600 hover:text-teal-700 font-medium hover:underline transition-colors"
                    >
                      {isLogin ? "New here? Create an account \u2192" : "Already a hero? Sign in \u2192"}
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Features Section with Icons */}
          <div className="grid md:grid-cols-3 gap-8 mt-20">
            {[
              {
                icon: MapPin,
                title: 'Location Tracking',
                description: 'Pin exact locations where animals are spotted using interactive maps with auto-detection.',
                delay: '0s',
                color: 'from-blue-400 to-blue-600'
              },
              {
                icon: Users,
                title: 'Nearby Alerts',
                description: 'Automatically notify rescuers within 50km radius via email when new animals need help.',
                delay: '0.2s',
                color: 'from-purple-400 to-purple-600'
              },
              {
                icon: MessageCircle,
                title: 'Real-Time Chat',
                description: 'Connect instantly with spotters through real-time messaging to coordinate rescue efforts.',
                delay: '0.4s',
                color: 'from-pink-400 to-pink-600'
              }
            ].map((feature, idx) => (
              <Card
                key={idx}
                className="text-center shadow-xl border-0 backdrop-blur-sm bg-white/90 transform hover:scale-110 hover:shadow-2xl transition-all duration-300 animate-fadeIn group"
                style={{ animationDelay: feature.delay }}
              >
                <CardContent className="pt-8 pb-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center group-hover:animate-bounce`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-fadeIn {
          animation: fadeIn 1s ease-out forwards;
          opacity: 0;
        }
        .animate-slideDown {
          animation: slideDown 0.8s ease-out forwards;
        }
        .animate-slideRight {
          animation: slideRight 0.6s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out forwards;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForgotPassword(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
            <h3 className="text-xl font-bold mb-2">Reset Your Password</h3>
            <p className="text-gray-600 text-sm mb-4">Enter your email and we'll send you a link to reset your password.</p>
            <form onSubmit={handleForgotPassword}>
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="mb-4"
                required
              />
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)} className="flex-1">Cancel</Button>
                <Button type="submit" data-testid="send-reset-btn" className="flex-1 text-white" style={{ backgroundColor: 'rgb(26, 160, 157)' }} disabled={forgotLoading}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
